/**
 * Settings Types
 *
 * Type definitions for Settings and Profile pages.
 * Based on SETTINGS_FUNCTIONAL_SPEC.md and ACL v2.3 schema.
 */

// =============================================================================
// AUTHORIZATION TYPES (from v2.3 ACL schema)
// =============================================================================

export type CaseFileAuthority =
  | 'patient_self'
  | 'parent_of_minor'
  | 'legal_guardian'
  | 'designated_agent';

export type CaseFileLockReason =
  | 'pending_patient_authorization'
  | 'pending_adult_transition'
  | 'authorization_revoked';

export type CaseFilePermission = 'owner' | 'admin' | 'editor' | 'viewer' | 'logger';

// =============================================================================
// RELATIONSHIP TYPES
// =============================================================================

export type Relationship =
  | 'self'
  | 'parent'
  | 'child'
  | 'spouse'
  | 'sibling'
  | 'grandparent'
  | 'extended'
  | 'other';

export const RELATIONSHIP_LABELS: Record<Relationship, string> = {
  self: 'Self (Care Recipient)',
  parent: 'Parent',
  child: 'Adult Child',
  spouse: 'Spouse/Partner',
  sibling: 'Sibling',
  grandparent: 'Grandparent',
  extended: 'Extended Family',
  other: 'Other',
};

// =============================================================================
// CARE TEAM TYPES
// =============================================================================

export type CareTeamRole = 'admin' | 'editor' | 'viewer' | 'contact';

export const CARE_TEAM_ROLE_INFO: Record<CareTeamRole, { label: string; description: string }> = {
  admin: {
    label: 'Admin',
    description: 'Full edit access + manage team',
  },
  editor: {
    label: 'Editor',
    description: 'Can edit documents and care logs',
  },
  viewer: {
    label: 'Viewer',
    description: 'Read-only access to all information',
  },
  contact: {
    label: 'Contact Only',
    description: 'No system access - contact info stored for reference',
  },
};

export interface CareTeamMember {
  userId?: string;
  name: string;
  email?: string;
  relationship: Relationship;
  role: CareTeamRole | 'owner';
  addedAt?: { _seconds: number } | Date;
}

// =============================================================================
// PROVIDER TYPES
// =============================================================================

export type ProviderCategory = 'medical' | 'therapy' | 'school' | 'other';

export const PROVIDER_CATEGORY_LABELS: Record<ProviderCategory, string> = {
  medical: 'Medical',
  therapy: 'Therapy',
  school: 'School',
  other: 'Other',
};

export interface Provider {
  id?: string;
  name: string;
  specialty: string;
  category: ProviderCategory;
}

// =============================================================================
// INSURANCE TYPES
// =============================================================================

export interface InsuranceInfo {
  provider?: string;
  policyNumber?: string;
  groupNumber?: string;
  subscriberId?: string;
  planType?: string;
}

// =============================================================================
// AUTHORIZATION DOCUMENTATION TYPES
// =============================================================================

export interface AuthorityDocumentation {
  parentalAttestationId?: string;
  guardianshipDocumentId?: string;
  patientAuthorizationId?: string;
}

// =============================================================================
// SCHOOL INFO TYPES
// =============================================================================

export interface SchoolInfo {
  schoolName?: string;
  graduationYear?: number;
  hasIEP?: boolean;
  has504Plan?: boolean;
}

// =============================================================================
// CASEFILE TYPES
// =============================================================================

export interface CaseFileBasicInfo {
  name: string;
  preferredName?: string;
  dateOfBirth: string;
  primaryDiagnosis: string;
  notes?: string;
}

export interface CaseFile extends CaseFileBasicInfo {
  caseFileId: string;
  ownerId: string;
  providers: Provider[];
  careTeam: CareTeamMember[];
  insuranceInfo?: InsuranceInfo;
  schoolInfo?: SchoolInfo;
  authorityType?: CaseFileAuthority;
  authorityDocumentation?: AuthorityDocumentation;
  adultTransitionDate?: string;
  isLocked?: boolean;
  lockReason?: CaseFileLockReason;
  createdAt?: { _seconds: number } | Date;
  updatedAt?: { _seconds: number } | Date;
}

export interface CaseFileSummary {
  caseFileId: string;
  name: string;
  dateOfBirth: string;
  diagnoses: string[];
  userPermissions: CaseFilePermission;
  userRelationship?: string;
  isOwner: boolean;
  authorityType?: CaseFileAuthority;
  authorityDocumentation?: AuthorityDocumentation;
  isLocked?: boolean;
  lockReason?: CaseFileLockReason;
  createdAt?: { _seconds: number } | Date;
  updatedAt?: { _seconds: number } | Date;
}

// =============================================================================
// API RESPONSE TYPES
// =============================================================================

export interface ListCaseFilesResponse {
  success: boolean;
  caseFiles: CaseFileSummary[];
  totalCount: number;
  error?: string;
}

export interface GetCaseFileResponse {
  success: boolean;
  caseFile?: CaseFile;
  error?: string;
}

export interface UpdateCaseFileRequest {
  caseFileId: string;
  basicInfo?: Partial<CaseFileBasicInfo>;
  providers?: Provider[];
  insuranceInfo?: InsuranceInfo;
}

export interface UpdateCaseFileResponse {
  success: boolean;
  caseFile?: CaseFile;
  error?: string;
}

export interface ManageCareTeamRequest {
  caseFileId: string;
  operation: 'add' | 'update' | 'remove';
  userId?: string;
  memberData?: {
    name: string;
    email: string;
    relationship: Relationship;
    role: CareTeamRole;
  };
}

export interface ManageCareTeamResponse {
  success: boolean;
  careTeam?: CareTeamMember[];
  message?: string;
  error?: string;
}

// =============================================================================
// USER PROFILE TYPES
// =============================================================================

export interface UserProfile {
  displayName: string;
  email: string;
  phoneNumber?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

export interface UpdateUserProfileRequest {
  displayName?: string;
  phoneNumber?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

export interface UpdateUserProfileResponse {
  success: boolean;
  error?: string;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

export function formatAuthorityType(type?: CaseFileAuthority): string {
  if (!type) return 'Not set';
  const labels: Record<CaseFileAuthority, string> = {
    patient_self: 'Self-Managed Account',
    parent_of_minor: 'Parent of Minor',
    legal_guardian: 'Legal Guardian',
    designated_agent: 'Designated Agent',
  };
  return labels[type] || type;
}

export function formatAuthorityTypeShort(type?: CaseFileAuthority): string {
  if (!type) return 'Not set';
  const labels: Record<CaseFileAuthority, string> = {
    patient_self: 'Self',
    parent_of_minor: 'Parent',
    legal_guardian: 'Guardian',
    designated_agent: 'Designated Agent',
  };
  return labels[type] || type;
}

export function formatLockReason(reason?: CaseFileLockReason): string {
  if (!reason) return '';
  const labels: Record<CaseFileLockReason, string> = {
    pending_patient_authorization: 'Pending patient authorization',
    pending_adult_transition: 'Pending adult transition',
    authorization_revoked: 'Authorization revoked',
  };
  return labels[reason] || reason;
}

export function getAuthStatusDisplay(type?: CaseFileAuthority): { title: string; color: 'green' | 'orange' } {
  if (!type) return { title: 'Pending Authorization', color: 'orange' };

  const statusMap: Record<CaseFileAuthority, { title: string; color: 'green' | 'orange' }> = {
    patient_self: { title: 'Self-Managed Account', color: 'green' },
    parent_of_minor: { title: 'Authorized as Parent', color: 'green' },
    legal_guardian: { title: 'Authorized as Legal Guardian', color: 'green' },
    designated_agent: { title: 'Authorized by Patient', color: 'green' },
  };

  return statusMap[type] || { title: 'Pending Authorization', color: 'orange' };
}

export function getRoleBadgeStyle(role?: string): { bg: string; text: string } {
  switch (role) {
    case 'owner':
      return { bg: 'bg-evergreen', text: 'text-white' };
    case 'admin':
      return { bg: 'bg-slate-ink', text: 'text-white' };
    case 'editor':
      return { bg: 'bg-soft-neutral', text: 'text-deep-forest' };
    case 'viewer':
      return { bg: 'bg-gray-100', text: 'text-gray-600' };
    default:
      return { bg: 'bg-gray-100', text: 'text-gray-600' };
  }
}

export function getProviderCategoryStyle(category: ProviderCategory): { bg: string; text: string } {
  switch (category) {
    case 'medical':
      return { bg: 'bg-green-100', text: 'text-green-800' };
    case 'therapy':
      return { bg: 'bg-soft-neutral', text: 'text-deep-forest' };
    case 'school':
      return { bg: 'bg-soft-neutral', text: 'text-deep-forest' };
    case 'other':
      return { bg: 'bg-soft-neutral', text: 'text-deep-forest' };
    default:
      return { bg: 'bg-soft-neutral', text: 'text-deep-forest' };
  }
}

export function formatTimestampToDate(timestamp?: { _seconds: number } | Date): Date | null {
  if (!timestamp) return null;
  if (timestamp instanceof Date) return timestamp;
  if ('_seconds' in timestamp) {
    return new Date(timestamp._seconds * 1000);
  }
  return null;
}

// =============================================================================
// CONSENT TYPES (Washington MHMDA Compliance)
// =============================================================================

/**
 * Current consent version - increment when consent text changes
 */
export const CURRENT_CONSENT_VERSION = '1.0';

/**
 * Platform consent text displayed to users
 */
export const PLATFORM_CONSENT_TEXT =
  'I consent to Hesvara collecting and using my health-related information ' +
  'to provide caregiving organization and advocacy services. I understand that ' +
  'I can withdraw this consent at any time by deleting my account.';

/**
 * Types of consent that can be granted
 */
export type ConsentType = 'platform_consent';

/**
 * Consent record stored in Firestore
 */
export interface ConsentRecord {
  consentId: string;
  consentType: ConsentType;
  granted: boolean;
  grantedAt?: { _seconds: number } | Date;
  withdrawnAt?: { _seconds: number } | Date;
  version: string;
  consentText: string;
  ipAddress?: string;
  userAgent?: string;
}

/**
 * Request to record a new consent
 */
export interface RecordConsentRequest {
  consentType: ConsentType;
  granted: boolean;
  version: string;
  consentText: string;
}

/**
 * Response from recordConsent function
 */
export interface RecordConsentResponse {
  success: boolean;
  consentId?: string;
  error?: string;
}

/**
 * Response from getConsents function
 */
export interface GetConsentsResponse {
  success: boolean;
  consents: ConsentRecord[];
  error?: string;
}

/**
 * Response from hasValidConsent function
 */
export interface HasValidConsentResponse {
  valid: boolean;
  version?: string;
}
